import { useProjectLightBox } from 'common/hook/useProjectLightBox';
import { IAnyType, IServerProps } from 'common/types/common';
import { IProjectItem, IProjectItemOverview } from 'common/types/projects';
import { getValidatedDynamicParam } from 'common/utils/dynamicRoute';
import { buildBreadcrumbListSchema } from 'common/utils/structuredData';
import { buildAbsoluteUrl } from 'common/utils/url';
import { Layout } from 'layout';
import { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const CTA = dynamic(() => import('common/components/Cta').then(mod => mod.CTA));
const HeroProject = dynamic(() => import('modules/work/HeroProject').then(mod => mod.HeroProject));
const ProjectContent = dynamic(() =>
  import('modules/work/ProjectContent').then(mod => mod.ProjectContent),
);
const WorkProjectsOther = dynamic(() =>
  import('modules/work/WorkProjectsOther').then(mod => mod.WorkProjectsOther),
);

const WorkSinglePage = (pageProps: IWorkSinglePageProps) => {
  const { i18n, t } = useTranslation([
    pageProps.data.detailsFileName,
    'projectItemsOverview',
    'common',
  ]);
  const router = useRouter();

  const projectsData = t('projectItemsOverview:projectItems', {
    returnObjects: true,
    defaultValue: [],
  }) as unknown;
  let projectsOther = Array.isArray(projectsData) ? (projectsData as IProjectItemOverview[]) : [];

  const project: IProjectItem | undefined = i18n.getResourceBundle(
    i18n.language,
    pageProps.data.detailsFileName,
  );

  const projectItemOverview: IProjectItemOverview | undefined = projectsOther.find(item => {
    return item.slug === `${t('common:ourWorkSlug')}/${router.query.key}`;
  });
  projectsOther = projectsOther.filter(item => item.slug !== project?.slug && !!item.active);

  const seo = {
    alternate: projectItemOverview?.seo.alternate,
    description: projectItemOverview?.seo.description,
    image: projectItemOverview?.seo.image,
    slug: projectItemOverview?.slug,
    title: projectItemOverview?.seo.title,
  };

  const base = t('common:company.url');
  const menuItems = t('common:menu.items', { returnObjects: true }) as Array<{
    link: string;
    title: string;
  }>;
  const homeLabel = menuItems.find(item => item.link === '/')?.title || 'Home';
  const workLabel =
    menuItems.find(item => item.link === t('common:ourWorkSlug'))?.title || 'Case studies';
  const workListUrl = buildAbsoluteUrl(base, t('common:ourWorkSlug'));
  const pageUrl = projectItemOverview?.slug
    ? buildAbsoluteUrl(base, projectItemOverview.slug)
    : workListUrl;
  const publisher = {
    '@type': 'Organization',
    name: 'Nordit d.o.o.',
    url: base,
    logo: {
      '@type': 'ImageObject',
      url: 'https://firebasestorage.googleapis.com/v0/b/nordit-83a0b.appspot.com/o/logo%2FNordit%20logo%20-%20primary%20white%2FNordit%20logo%20-%20primary%20white%402x.png?alt=media&token=d979ae78-414f-4fd1-bfb5-dc31ad8ce3cf',
    },
  };
  const breadcrumbSchema = buildBreadcrumbListSchema([
    { name: homeLabel, url: buildAbsoluteUrl(base, '/') },
    { name: workLabel, url: workListUrl },
    {
      name: projectItemOverview?.seo.title || project?.title || 'Case study',
      url: pageUrl,
    },
  ]);
  const caseStudySchema = projectItemOverview
    ? {
        '@type': 'Article',
        mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
        headline: projectItemOverview.seo.title,
        image: projectItemOverview.seo?.image,
        description: projectItemOverview.seo?.description,
        author: {
          '@type': 'Person',
          name: projectItemOverview.author.name,
          url: projectItemOverview.author.url || undefined,
        },
        publisher,
        datePublished: projectItemOverview.date,
        dateModified: projectItemOverview.date,
        inLanguage: i18n.language,
        url: pageUrl,
        isPartOf: {
          '@type': 'CollectionPage',
          name: `${workLabel} | Nordit`,
          url: workListUrl,
        },
        about: (projectItemOverview.services || []).map(service => ({
          '@type': 'Thing',
          name: service.label,
          url: service.href ? buildAbsoluteUrl(base, service.href) : undefined,
        })),
        keywords: (projectItemOverview.services || []).map(service => service.label).join(', '),
      }
    : null;
  const schemaMarkup = [caseStudySchema, breadcrumbSchema].filter(Boolean);
  const seoWithSchema = { ...seo, schemaMarkup };

  const { openLightBox, renderLightbox } = useProjectLightBox({ project });
  return (
    <>
      <Layout className="page-light" seo={seoWithSchema}>
        {!!project && <HeroProject onImageClick={openLightBox} project={project} />}
        {!!project && <ProjectContent onImageClick={openLightBox} project={project} />}
        <CTA
          dark={!!project?.testimonial?.visible}
          subtitle={t('common:projectCtaSection.gotAProject')}
          text={t('common:projectCtaSection.text')}
          title={t('common:projectCtaSection.letsDiscussYourIdea')}
        />
        <WorkProjectsOther projects={projectsOther} />
      </Layout>

      {renderLightbox()}
    </>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
): Promise<IServerProps<IWorkSinglePageProps>> => {
  const key = getValidatedDynamicParam(context.query.key);

  if (!key) {
    return {
      notFound: true,
    };
  }

  const tempLocale: IAnyType = await serverSideTranslations(context.locale || 'en', [
    'common',
    'projectItemsOverview',
  ]);

  const workItems: IAnyType[] =
    tempLocale._nextI18Next.initialI18nStore[context.locale || 'en']?.projectItemsOverview
      ?.projectItems || [];

  const workItem = workItems.find(
    item =>
      item.slug ===
      `${
        tempLocale._nextI18Next.initialI18nStore[context.locale || 'en']?.common.ourWorkSlug
      }/${key}`,
  );

  const locale: IAnyType = await serverSideTranslations(context.locale || 'en', [
    workItem?.detailsFileName || '',
    'common',
    'meta',
    'projectItemsOverview',
  ]);

  if (!workItem?.detailsFileName) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      data: {
        detailsFileName: workItem?.detailsFileName || '',
        referer: context.req.headers.referer || null,
      },
      ...locale,
    },
  };
};

interface IWorkSinglePageProps {
  data: { detailsFileName: string; referer?: string | null } | Record<string, never>;
}

export default WorkSinglePage;
